import React from 'react';
import { PROVIDERS, GABSOR_YEAR_USD, GABSOR_YEAR_EUR, AXIS_MAX, eur } from './data/gdprRepPrices.js';
import { FROM_PRICE, usd } from '../../lib/pricing.js';

// Dot plot of eight providers' published yearly prices (entry plan) on a euro
// axis, with Gabsor's yearly price marked. Data and conversions:
// data/gdprRepPrices.js. Filled dot: an EU + UK price; hollow dot: the
// provider publishes no EU + UK price, so its EU price is shown. On phones the shared <Infographic>
// scroll wrapper centres the element marked data-scroll-center (Gabsor).

const C = { axis: '#cbd5e1', tick: '#5b6472', dot: '#6b7a93', navy: '#003399', white: '#ffffff' };
const X0 = 40;
const X1 = 600;
const AXIS_Y = 112;
const R = 7;
const STACK = 18;

const x = (v) => Math.round((X0 + (v / AXIS_MAX) * (X1 - X0)) * 10) / 10;

// Stack dots that would overlap: each goes to the lowest free level.
const placed = (() => {
  const out = [];
  for (const p of PROVIDERS) {
    const cx = x(p.eur);
    let level = 0;
    while (out.some((q) => q.level === level && Math.abs(q.cx - cx) < 2 * R + 1)) level += 1;
    out.push({ ...p, cx, level, cy: AXIS_Y - level * STACK });
  }
  return out;
})();

const TICKS = Array.from({ length: 6 }, (_, i) => (AXIS_MAX / 5) * i);
const GX = x(GABSOR_YEAR_EUR);
const LABEL = `Gabsor ${usd(GABSOR_YEAR_USD)} / yr`;
const PILL_W = 132;
const PILL_X = Math.min(Math.max(GX - PILL_W / 2, 4), 640 - PILL_W - 4);

const DESC = [
  `Dot plot on a euro axis from ${eur(0)} to ${eur(AXIS_MAX)}.`,
  ...PROVIDERS.map((p) => `${p.id}: ${p.from ? 'from ' : ''}${eur(p.eur)} a year, ${p.euUk ? 'EU and UK' : 'EU price; no EU + UK price published'}.`),
  `Gabsor: ${usd(GABSOR_YEAR_USD)} a year, about ${eur(GABSOR_YEAR_EUR)}, EU and UK.`,
].join(' ');

const PriceDotPlot = () => (
  <svg viewBox="0 0 640 150" width="100%" role="img" aria-labelledby="pdp-title pdp-desc" xmlns="http://www.w3.org/2000/svg">
    <title id="pdp-title">Published yearly prices of eight GDPR representative providers, with Gabsor's price</title>
    <desc id="pdp-desc">{DESC}</desc>

    <line x1={X0} y1={AXIS_Y} x2={X1} y2={AXIS_Y} stroke={C.axis} strokeWidth="2" />
    {TICKS.map((t) => (
      <g key={t}>
        <line x1={x(t)} y1={AXIS_Y - 6} x2={x(t)} y2={AXIS_Y + 6} stroke={C.axis} strokeWidth="1.5" />
        <text x={x(t)} y={AXIS_Y + 28} textAnchor="middle" fontFamily="inherit" fontSize={12} fill={C.tick}>{eur(t)}</text>
      </g>
    ))}

    {placed.map((p) => (
      <circle key={p.id} cx={p.cx} cy={p.cy} r={R} fill={p.euUk ? C.dot : C.white} stroke={C.dot} strokeWidth="2" />
    ))}

    <g data-scroll-center="">
      <line x1={GX} y1={42} x2={GX} y2={AXIS_Y} stroke={C.navy} strokeWidth="2" />
      <circle cx={GX} cy={AXIS_Y} r={9} fill={C.navy} />
      <rect x={PILL_X} y={14} width={PILL_W} height={28} rx={7} fill={C.navy} />
      <text x={PILL_X + PILL_W / 2} y={33} textAnchor="middle" fontFamily="inherit" fontSize={13} fontWeight="600" fill={C.white}>{LABEL}</text>
    </g>
  </svg>
);

const Swatch = ({ fill, stroke, r = 6 }) => (
  <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false">
    <circle cx="8" cy="8" r={r} fill={fill} stroke={stroke} strokeWidth="2" />
  </svg>
);

// Legend, rendered by <Infographic legend={...}> outside the scroll area so
// it stays visible on phones.
export const PriceDotPlotLegend = () => (
  <ul className="rs-figure-legend">
    <li><Swatch fill={C.dot} stroke={C.dot} />Provider, EU + UK price</li>
    <li><Swatch fill={C.white} stroke={C.dot} />EU only (no EU + UK price published)</li>
    <li><Swatch fill={C.navy} stroke={C.navy} />Gabsor Essential, {usd(FROM_PRICE)}/month billed annually</li>
  </ul>
);

export default PriceDotPlot;
